/**
 * Mention Utilities
 * Functions for parsing and rendering @mentions in text
 */

/**
 * Parse mentions from text
 * Converts @Name or @Name[uuid] format to an array of user IDs
 *
 * @param {string} text - The text containing mentions
 * @returns {string[]} Array of mentioned user IDs
 *
 * @example
 * parseMentions("Hey @John Smith[abc-123] and @Jane Doe[def-456]")
 * // Returns: ['abc-123', 'def-456']
 */
export function parseMentions(text) {
  if (!text) return [];

  // Match @Name[uuid] pattern
  const mentionRegex = /@[^[]+\[([a-f0-9-]+)\]/g;
  const mentions = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[1]); // Extract the UUID
  }

  return mentions;
}

/**
 * Convert display mentions to storage format
 * Converts @Name to @Name[uuid] using a mention map
 *
 * @param {string} text - The text with display mentions
 * @param {Object} mentionMap - Map of display names to user IDs
 * @returns {string} Text with UUIDs embedded
 *
 * @example
 * convertMentionsForStorage("Hi @John Smith", {"John Smith": "abc-123"})
 * // Returns: "Hi @John Smith[abc-123]"
 */
export function convertMentionsForStorage(text, mentionMap) {
  if (!text || !mentionMap) return text;

  let result = text;
  Object.entries(mentionMap).forEach(([displayName, userId]) => {
    const displayMention = `@${displayName}`;
    const fullMention = `@${displayName}[${userId}]`;
    // Use split and join for safe string replacement
    result = result.split(displayMention).join(fullMention);
  });

  return result;
}

/**
 * Render mentions in text as JSX elements
 * Converts @Name[uuid] to highlighted spans
 *
 * @param {string} text - The text containing mentions in @Name[uuid] format
 * @returns {Array} Array of text and JSX elements
 *
 * @example
 * renderMentions("Hi @John Smith[abc-123]!")
 * // Returns: ['Hi ', <span>@John Smith</span>, '!']
 */
export function renderMentions(text) {
  if (!text) return [];

  // Match @Name[uuid] pattern
  const mentionRegex = /@([^[]+)\[([a-f0-9-]+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add mention as JSX element
    const displayName = match[1];
    const userId = match[2];
    parts.push({
      type: 'mention',
      displayName,
      userId,
      key: `mention-${userId}-${match.index}`
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}

/**
 * Get unique mentioned user IDs from text
 * Useful for deduplication before sending notifications
 *
 * @param {string} text - The text containing mentions
 * @returns {string[]} Array of unique user IDs
 *
 * @example
 * getUniqueMentions("@John[abc] @Jane[def] @John[abc]")
 * // Returns: ['abc', 'def']
 */
export function getUniqueMentions(text) {
  const mentions = parseMentions(text);
  return [...new Set(mentions)];
}

/**
 * Check if text contains any mentions
 *
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains mentions
 *
 * @example
 * hasMentions("Hi @John[abc-123]") // true
 * hasMentions("Hello world") // false
 */
export function hasMentions(text) {
  if (!text) return false;
  return /@[^[]+\[[a-f0-9-]+\]/.test(text);
}

/**
 * Remove UUIDs from mentions for display
 * Converts @Name[uuid] to @Name
 *
 * @param {string} text - Text with UUIDs
 * @returns {string} Text without UUIDs
 *
 * @example
 * stripMentionUUIDs("Hi @John Smith[abc-123]!")
 * // Returns: "Hi @John Smith!"
 */
export function stripMentionUUIDs(text) {
  if (!text) return '';
  return text.replace(/@([^[]+)\[[a-f0-9-]+\]/g, '@$1');
}
